/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import {
  Auth,
  UserCredential,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  updateProfile,
  verifyPasswordResetCode
} from '@angular/fire/auth';
import { DocumentData, Firestore, doc, docData, setDoc, collection } from '@angular/fire/firestore';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom, of } from 'rxjs';
import { OSKUpdateProfileDTO, OSKUser } from '../../../types/user/user.type';

@Injectable({
  providedIn: 'root'
})
export class OSKFirebaseAuthService {
  private readonly auth: Auth = inject(Auth);
  private readonly firestore: Firestore = inject(Firestore);
  private readonly window = inject(DOCUMENT).defaultView;
  private readonly cookieService: CookieService = inject(CookieService);

  signOut(): Promise<void> {
    return signOut(this.auth);
  }

  signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  signUpWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  verifyPasswordResetCode(oobCode: string): Promise<string> {
    return verifyPasswordResetCode(this.auth, oobCode);
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }

  resetPassword(oobCode: string, password: string): Promise<void> {
    return confirmPasswordReset(this.auth, oobCode, password);
  }
  signInWithCustomToken(token: string): Promise<UserCredential> {
    return signInWithCustomToken(this.auth, token);
  }

  signUpWithEmailLink(email: string, continueUrl?: string): Promise<boolean> {
    const actionCodeSettings = {
      // URL you want to redirect back to. The domain (www.example.com) for this
      // URL must be in the authorized domains list in the Firebase Console.
      url: this.window?.location.href + '/verifyCode' || '',
      // continueUrl: 'http://localhost:4200' // This is appended to the url and can be used after confirmation,
      // This must be true.
      handleCodeInApp: true
      // iOS: {
      //   bundleId: 'com.example.ios'
      // },
      // android: {
      //   packageName: 'com.example.android',
      //   installApp: true,
      //   minimumVersion: '12'
      // },
      // dynamicLinkDomain: 'example.page.link'
    };

    return sendSignInLinkToEmail(getAuth(), email, actionCodeSettings)
      .then(() => {
        this.cookieService.set('emailForSignIn', email);
        return true;
      })
      .catch((error) => {
        // const errorCode = error.code;
        // const errorMessage = error.message;
        return false;
      });
  }

  confirmSignIn(): Promise<UserCredential | null> {
    if (isSignInWithEmailLink(this.auth, this.window?.location.href || '')) {
      let email: string = this.cookieService.get('emailForSignIn');

      if (!email) {
        // User opened the link on a different device. To prevent session fixation
        // attacks, ask the user to provide the associated email again. For example:
        email = window.prompt('Please provide your email for confirmation') || '';
      }

      return signInWithEmailLink(this.auth, email, this.window?.location.href || '')
        .then((user) => {
          this.cookieService.delete('emailForSignIn');
          return user;
        })
        .catch((error) => {
          return null;
          // Some error occurred, you can inspect the code: error.code
          // Common errors could be invalid email and invalid or expired OTPs.
        });
    } else {
      return firstValueFrom(of(null));
    }
  }

  setDoc(docDTO: DocumentData, path: string, ...pathSegments: string[]): Promise<void> {
    const docRef = doc(this.firestore, path, ...pathSegments);
    return setDoc(docRef, <DocumentData>docDTO);
  }

  updateProfile(profileDTO: OSKUpdateProfileDTO): Promise<void> {
    if (!this.auth.currentUser) {
      return Promise.reject();
    }
    return updateProfile(this.auth.currentUser, profileDTO);
  }

  async getUserByUid(uid: string): Promise<OSKUser | undefined> {
    const docRef = doc(this.firestore, 'users', uid);
    const userDocument: OSKUser | undefined = <OSKUser | undefined>(
      await firstValueFrom(docData(docRef))
    );
    return userDocument;
  }
  // Generates an organization id in Firestore
  generateOrganizationId(): string {
    return doc(collection(this.firestore, 'organizations')).id;
  }
}
